/**
 * 支付结果解析 / 支付宝扫码轮询（open 域 /payment-record/scan_alipay）
 */
import billingApi from './billingApi'

function unwrapData(res) {
  if (res == null) return null
  if (res.data != null && typeof res.data === 'object' && !Array.isArray(res.data)) {
    return res.data
  }
  return res
}

export function unwrapOrderPayload(res) {
  let d = unwrapData(res)
  for (let i = 0; i < 5; i++) {
    if (!d || typeof d !== 'object' || Array.isArray(d)) break
    if (d.order || d.pay_config || d.payConfig || d.payment_record || d.paymentRecord) break
    if (d.data != null && typeof d.data === 'object' && !Array.isArray(d.data)) {
      d = d.data
      continue
    }
    break
  }
  return d && typeof d === 'object' ? d : {}
}

function parseMaybeJsonObject(raw) {
  if (raw == null) return null
  if (typeof raw === 'object') return raw
  const s = String(raw).trim()
  if (!s) return null
  if (s.startsWith('{') || s.startsWith('[')) {
    try {
      return JSON.parse(s)
    } catch (e) {
      return null
    }
  }
  return null
}

function pickPayQrCodeString(source, depth = 0) {
  if (depth > 10 || source == null) return ''
  if (typeof source === 'string') {
    const s = source.trim()
    if (!s) return ''
    const parsed = parseMaybeJsonObject(s)
    if (parsed) return pickPayQrCodeString(parsed, depth + 1)
    if (s.startsWith('alipays://') || /^https?:\/\//i.test(s)) return s
    if (s.length > 12 && !s.includes('<')) return s
    return ''
  }
  if (typeof source !== 'object') return ''
  const keys = ['qr_code', 'qrcode', 'qrCode', 'code_url', 'codeUrl', 'pay_string', 'payString']
  for (let i = 0; i < keys.length; i++) {
    const v = source[keys[i]]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  const nestedKeys = [
    'pay_config',
    'payConfig',
    'data',
    'order',
    'payment_record',
    'paymentRecord',
    'alipay_trade_precreate_response'
  ]
  for (let i = 0; i < nestedKeys.length; i++) {
    const k = nestedKeys[i]
    if (source[k] != null) {
      const hit = pickPayQrCodeString(source[k], depth + 1)
      if (hit) return hit
    }
  }
  if (Array.isArray(source)) {
    for (let i = 0; i < source.length; i++) {
      const hit = pickPayQrCodeString(source[i], depth + 1)
      if (hit) return hit
    }
  }
  return ''
}

function normalizeVipOrderResponse(orderRes) {
  const root = orderRes || {}
  const inner =
    root.order && typeof root.order === 'object' && !Array.isArray(root.order) ? root.order : null
  const pr =
    root.payment_record ||
    root.paymentRecord ||
    (inner && (inner.payment_record || inner.paymentRecord)) ||
    {}
  let payConfig =
    root.pay_config ||
    root.payConfig ||
    (inner && (inner.pay_config || inner.payConfig)) ||
    (pr && (pr.pay_config || pr.payConfig)) ||
    {}
  if (typeof payConfig === 'string') {
    payConfig = parseMaybeJsonObject(payConfig) || { qr_code: payConfig }
  }
  return { root, order: inner, payConfig, paymentRecord: pr }
}

function centsToYuanDisplay(cents) {
  const n = Number(cents)
  if (!Number.isFinite(n)) return null
  if (n <= 0) return '0'
  if (n >= 100) return String(Math.round(n / 100))
  return String(Math.round(n))
}

function parsePayExpireSeconds(payConfig) {
  const pc = payConfig || {}
  const now = Math.floor(Date.now() / 1000)
  if (pc.expire_time != null) {
    const t = Number(pc.expire_time)
    if (t > 1e12) {
      const diff = Math.floor(t / 1000) - now
      if (diff > 0) return diff
    } else if (t > now) {
      return Math.floor(t - now)
    } else if (t > 0 && t < 86400) {
      return Math.floor(t)
    }
  }
  if (pc.expire_seconds != null) {
    const s = Number(pc.expire_seconds)
    if (s > 0) return Math.floor(s)
  }
  return 15 * 60
}

export function extractVipOrderId(orderRes) {
  const { root, order: inner, payConfig: pc } = normalizeVipOrderResponse(orderRes)
  const pr = root.payment_record || root.paymentRecord || pc.payment_record || {}
  const candidates = [
    pc.order_id,
    inner && inner.id,
    inner && inner.order_id,
    pc.payment_record_id,
    pc.payment_id,
    pc.id,
    pr.id,
    pr.order_id,
    root.order_id,
    root.payment_record_id,
    root.id
  ]
  for (let i = 0; i < candidates.length; i++) {
    const v = candidates[i]
    if (v != null && v !== '') return v
  }
  return null
}

export function extractVipPayQrCode(orderRes) {
  const direct = pickPayQrCodeString(orderRes)
  if (direct) return direct
  const { payConfig: pc } = normalizeVipOrderResponse(orderRes)
  return pc.qr_code || pc.qrcode || ''
}

export function extractVipOrderPayUrl(orderRes) {
  const o = orderRes || {}
  const nested = o.pay && typeof o.pay === 'object' ? o.pay : null
  const candidates = [
    o.pay_url,
    o.payment_url,
    o.h5_url,
    o.mweb_url,
    o.cashier_url,
    o.redirect_url,
    o.url,
    nested && nested.url,
    nested && nested.h5_url,
    nested && nested.mweb_url
  ]
  for (let i = 0; i < candidates.length; i++) {
    const u = candidates[i]
    if (typeof u === 'string' && /^https?:\/\//i.test(u)) return u
  }
  return null
}

export function resolveVipOrderPayment(orderRes) {
  const qrCode = extractVipPayQrCode(orderRes)
  const payUrl = extractVipOrderPayUrl(orderRes)
  const orderId = extractVipOrderId(orderRes)
  const { root } = normalizeVipOrderResponse(orderRes)
  const payType = root.pay_type || root.payType || ''
  const needPay = root.is_need_pay != null ? Number(root.is_need_pay) : null
  return { qrCode, payUrl, orderId, payType, needPay }
}

export function getVipOrderPaymentFailureMessage(orderRes, fallback = '未获取到支付二维码，请稍后重试') {
  const { needPay, payType } = resolveVipOrderPayment(orderRes)
  if (needPay === 0) return '订单无需支付，请刷新积分余额'
  if (payType === 'select_pending') return '请在 App 内选择支付方式后完成购买'
  const root = orderRes && typeof orderRes === 'object' ? orderRes : {}
  const msg = root.message || root.msg || root.error
  if (typeof msg === 'string' && msg.trim()) return msg.trim()
  return fallback
}

export function buildCreditsPayModalPayload(orderRes, pack) {
  const { order: inner, payConfig: pc, root } = normalizeVipOrderResponse(orderRes)
  const order = inner || root
  const qrCode = extractVipPayQrCode(orderRes)
  const payMoney =
    centsToYuanDisplay(order.pay_money) ||
    centsToYuanDisplay(order.money_price) ||
    centsToYuanDisplay(root.pay_money) ||
    (pack && pack.price != null ? String(pack.price) : '0')
  const points = pack && pack.points != null ? Number(pack.points) : 0
  return {
    orderId: extractVipOrderId(orderRes),
    title: points > 0 ? `积分充值 ${points.toLocaleString('en-US')} 积分` : '积分充值',
    priceMain: String(payMoney),
    qrCode,
    expireSeconds: parsePayExpireSeconds(pc)
  }
}

export async function scanAlipayPayment(orderId) {
  if (orderId == null || orderId === '') {
    throw new Error('缺少订单号')
  }
  return billingApi.get('/payment-record/scan_alipay', {
    params: { order_id: orderId }
  })
}

export function isAlipayPaymentPaid(res) {
  if (res == null) return false
  const c = res.code
  if (c !== undefined && c !== null) {
    const n = Number(c)
    if (n !== 0 && n !== 200) return false
  }
  const d = res.data
  if (d === 0 || d === false || d === '0' || d === 'unpaid' || d === 'pending') return false
  if (d === 1 || d === true || d === '1' || d === 'paid' || d === 'success') return true
  if (d === 2 || d === '2') return true
  if (typeof d === 'object' && d) {
    const st = d.status != null ? String(d.status).toLowerCase() : ''
    if (st === 'paid' || st === 'success' || st === 'completed') return true
    if (d.paid === 1 || d.paid === true || d.is_paid === 1 || d.is_paid === true) return true
    const ps = Number(d.pay_status)
    if (ps === 1 || ps === 2) return true
  }
  return false
}

export function getApiErrorMessage(err, fallback = '请求失败') {
  if (!err) return fallback
  const d = err.response && err.response.data
  if (d && (d.message || d.msg)) return String(d.message || d.msg)
  if (err.message) return err.message
  return fallback
}
